import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import DataTable from "../../Common/DataTable/DataTable";
import styles from "./report.module.css";
import CustomSpinner from "../../Common/Spinner";
import { UserReportColumns } from "../../utils/tableHeader";
import {
  getUsersPartnerList,
  getPaymentReportDownloadApi,
  getUserReportDownloadApi,
  paymentReport,
  userReport,
  planDeductionReport
} from "../../api";
import { format } from "date-fns";
import { addDays } from "date-fns";
import { Filter } from "../../Common";
import withFeatureActions from "../../withFeatureActions";
import { productType } from "../../utils/utils";
import { CookieUtils } from "../../axios.config";

function UserReport({ featureAccess }) {
  const clientName = CookieUtils.get("clientName");
  const defaultPartner = clientName === "STOCKAL" ? "ALL" : clientName;
  const { readAccess, writeAccess, modifyAccess } = featureAccess;
  const [reportList, setReportList] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [initialFetch, setInitialFetch] = useState({
    "User Reports": false,
    "Payment Reports": false,
    "Plan Deduction": true,
  });
  const date = new Date();
  const [startDate, setStartDate] = useState(addDays(date, -15));
  const [endDate, setEndDate] = useState(date);
  const [partner, setPartner] = useState(defaultPartner);
  const [partnerList, setPartnerList] = useState([]);
  const [fetchBy, setFetchBy] = useState("User Reports");
  const [timeoutId, setTimeoutId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [planDeductionMessage, setPlanDeductionMessage] = useState("");

  const downloadPlanDeductionReport = async () => {
    try {
      setDataLoading(true);
      setErrorMessage("");
      setPlanDeductionMessage("");
      const resp = await planDeductionReport();

      if (resp?.status === 200 && resp?.data) {

        const csv = resp.data;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "PlanDeductionReport.csv";
        link.click();

        URL.revokeObjectURL(url);
        setPlanDeductionMessage("Plan Deduction report downloaded successfully.");
        setDataLoading(false);
      } else {
        setErrorMessage("Failed to download Plan Deduction report");
        setDataLoading(false);
      }
    } catch (err) {
      setErrorMessage("Something went wrong while downloading report");
      setDataLoading(false);
    }
  };
  useEffect(() => {
    if (planDeductionMessage || errorMessage) {
      const timer = setTimeout(() => {
        setPlanDeductionMessage("");
        setErrorMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [planDeductionMessage, errorMessage]);

  const getPartnerApi = async () => {
    try {
      const resp = await getUsersPartnerList({ appName: productType.INVESTING });

      if (resp?.data?.code === 200) {
        const partners = resp?.data?.data.map((item) => ({
          name: item.partnerName,
          code: item.partnerCd,
        }));
        setPartnerList([{ name: "ALL", code: "ALL" }, ...partners]);
      }
    } catch (error) {}
  };
  const partnerOptions =
    partnerList?.map((partner) => ({
      label: partner.name,
      value: partner.code,
    })) || [];

  const getUserReportDownload = async (page = currentPage) => {
    try {
      const response = await getUserReportDownloadApi(
        page,
        pageSize,
        "User Reports",
        "Investing"
      );

      if (response?.data?.code === 200) {
        setInitialFetch((prev) => ({
          ...prev,
          "User Reports": true,
        }));

        if (
          fetchBy === "User Reports" &&
          response?.data?.data?.reports &&
          response?.data?.data?.reports.length > 0
        ) {
          if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
          }
          setReportList(response?.data?.data?.reports);
          const totalCount = response?.data?.data?.total || 0;
          setTotalPages(Math.ceil(totalCount / pageSize));
        }
        setIsGenerating(false);
        setDataLoading(false);
      }
    } catch (e) {
      setDataLoading(false);
      setIsGenerating(false);
    }
  };

  const getUserReport = async (
    dateStart,
    dateEnd,
    partnerValue,
    page = currentPage
  ) => {
    setDataLoading(true);
    try {
      const response = await userReport({
        startDate: dateStart,
        endDate: dateEnd,
        partner: partnerValue,
        page: page,
        pageSize: pageSize,
        filterType: "User Reports",
        source: "Investing",
      });
        if (response?.data?.status === 400 || response?.status === 400) {
          setErrorMessage(response?.data?.error?.[0] || "Something went wrong");
          setDataLoading(false);
          return;
        }
      if (response?.data?.code === 200 && response?.data?.data === true) {
        setIsGenerating(true);

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        const newTimeoutId = setTimeout(() => {
          if (fetchBy === "User Reports") {
            getUserReportDownload(page);
          }
          setTimeoutId(null);
        }, 10000);

        setTimeoutId(newTimeoutId);
      }else {
        setErrorMessage(response?.data?.error?.[0] || "Something went wrong");
        setDataLoading(false);
      }
    } catch (e) {
      setErrorMessage(e.response?.data?.error?.[0] || "Something went wrong");
      setDataLoading(false);
    }
  };

  const getPaymentReportDownload = async (page = currentPage) => {
    try {
      const response = await getPaymentReportDownloadApi(
        page,
        pageSize,
        "Payment Reports",
        "Investing"
      );

      if (response?.data?.code === 200) {
        setInitialFetch((prev) => ({
          ...prev,
          "Payment Reports": true,
        }));

        if (
          fetchBy === "Payment Reports" &&
          response?.data?.data &&
          response?.data?.data?.reports?.length > 0
        ) {
          if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
          }
          setReportList(response?.data?.data?.reports);
          const totalCount =
            response?.data?.data?.total || response?.data?.data?.length || 0;
          setTotalPages(Math.ceil(totalCount / pageSize));
        }
        setIsGenerating(false);
        setDataLoading(false);
      }
    } catch (e) {
      setDataLoading(false);
      setIsGenerating(false);
    }
  };

  const getPaymentReport = async (
    dateStart,
    dateEnd,
    partnerValue,
    page = currentPage
  ) => {
    setDataLoading(true);
    try {
      const response = await paymentReport({
        startDate: dateStart,
        endDate: dateEnd,
        partner: partnerValue,
        page: page,
        pageSize: pageSize,
        filterType: "Payment Reports",
        source: "Investing",
      });
      if (response?.data?.status === 400 || response?.status === 400) {
        setErrorMessage(response?.data?.error?.[0] || "Something went wrong");
        setDataLoading(false);
        return;
      }
      if (response?.data?.code === 200 && response?.data?.data === true) {
        setIsGenerating(true);

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        const newTimeoutId = setTimeout(() => {
          if (fetchBy === "Payment Reports") {
            getPaymentReportDownload(page);
          }
          setTimeoutId(null);
        }, 10000);

        setTimeoutId(newTimeoutId);
      } else {
        setDataLoading(false);
      }
    } catch (e) {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    getPartnerApi();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (!fetchBy) {
      setDataLoading(false);
      return;
    }

    setIsGenerating(false);
    setReportList([]);
    setCurrentPage(1);

    if (fetchBy === "User Reports") {
      setDataLoading(true);
      getUserReportDownload(1);
    } else if (fetchBy === "Payment Reports") {
      setDataLoading(true);
      getPaymentReportDownload(1);
    }else if (fetchBy === "Plan Deduction") {
      setDataLoading(false);
    }
  }, [fetchBy]);

  useEffect(() => {
    if (initialFetch[fetchBy] && startDate && endDate) {
      const dateStart = format(startDate, "yyyy-MM-dd");
      const dateEnd = format(endDate, "yyyy-MM-dd");

      setDataLoading(true);
      if (fetchBy === "User Reports") {
        getUserReportDownload(currentPage);
      } else if (fetchBy === "Payment Reports") {
        getPaymentReportDownload(currentPage);
      }
    }
  }, [currentPage]);

  const onSubmit = () => {
    setErrorMessage("");

    if (fetchBy === "Plan Deduction") {
      downloadPlanDeductionReport();  
      return;
    }

    if (!partner) {
    setErrorMessage("Partner is required");
    return;
   }
    if (!fetchBy) {
    setErrorMessage("Report Type is required");
    return;
   }
    if (startDate && endDate) {
      const dateStart = format(startDate, "yyyy-MM-dd");
      const dateEnd = format(endDate, "yyyy-MM-dd");

      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      setCurrentPage(1);
      if (fetchBy === "User Reports") {
        getUserReport(dateStart, dateEnd, partner, 1);
      } else if (fetchBy === "Payment Reports") {
        getPaymentReport(dateStart, dateEnd, partner, 1);
      }
    } else {
      setErrorMessage("Please Select Date");
    }
  };

  const fetchByOptions =
  clientName === "STOCKAL"
    ? {
        "User Reports": "User Reports",
        "Payment Reports": "Payment Reports",
        "Plan Deduction": "Plan Deduction",
      }
    : {
        "User Reports": "User Reports",
      };

  return (
    <div>
      {writeAccess && (
        <Card className={styles.customCard}>
          <Card.Body className={styles.customCardBody}>
            <Filter
              partner={partner}
              setPartner={setPartner}
              startDate={fetchBy === "Plan Deduction" ? null : startDate}
              endDate={fetchBy === "Plan Deduction" ? null : endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              onSearch={onSubmit}
              errorMessage={errorMessage}
              partnerList={partnerOptions}
              showStatus={false}
              fetchBy={fetchBy}
              setFetchBy={setFetchBy}
              showFetchBy={true}
              fetchByOptions={fetchByOptions}
              setErrorMessage={setErrorMessage}
              loading={dataLoading}
              disableFetchBy={clientName !== "STOCKAL"}
            />
          </Card.Body>
        </Card>
      )}
      {dataLoading ? (
        <>
          <CustomSpinner />
          {isGenerating && (
            <p
              style={{ color: "white", marginTop: "20px", textAlign: "center" }}
            >
              The report is being generated. Please check again shortly.
            </p>
          )}
        </>
      ) : Array.isArray(reportList) && reportList.length > 0 ? (
        <>
          <DataTable
            columns={UserReportColumns}
            list={reportList}
            rowClick={false}
            title={fetchBy}
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pagination={true}
          />
        </>
      ) : fetchBy === "Plan Deduction" ? (
        <>
          {planDeductionMessage ? (
            <p className={styles.SuccessMessage}>
              {planDeductionMessage}
            </p>
          ) : errorMessage ? (
            <p className={styles.ErrorMessage}>
              {errorMessage}
            </p>
          ) : null}
        </>
      ) : initialFetch[fetchBy] ? (
        <p className={styles.noDataMessage}>
          No Data Found
        </p>
      ) : (
        <CustomSpinner />
      )}
    </div>
  );
}

export default withFeatureActions(UserReport, "INVESTING", "Download");